import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { FileText, Download, Calendar, ChartBar, ArrowClockwise, Envelope } from '@phosphor-icons/react';
import { toast } from 'sonner';

const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_COLORS = {
  ENVIADO: 'bg-blue-100 text-blue-800',
  ENTREGADO: 'bg-green-100 text-green-800',
  APERTURADO: 'bg-emerald-100 text-emerald-800',
  CLICK: 'bg-teal-100 text-teal-800',
  RESPONDIDO: 'bg-violet-100 text-violet-800',
  RECHAZADO: 'bg-red-100 text-red-800',
  'MARCADO SPAM': 'bg-orange-100 text-orange-800',
  'PENDIENTE ENVIO MANUAL': 'bg-yellow-100 text-yellow-900 border border-yellow-400',
};

export default function CampaignTrackingPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [days, setDays] = useState(0); // 0 = all time
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/campaign-tracking/preview?days=${days}&limit=500`, AUTH());
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/campaign-tracking/history?limit=20`, AUTH());
      setHistory(res.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    loadPreview();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const downloadExcel = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/campaign-tracking/report.xlsx?days=${days}`, {
        ...AUTH(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Control_y_Seguimiento_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel descargado');
    } catch (e) {
      toast.error('Error descargando Excel');
    }
  };

  const sendWeekly = async () => {
    if (!window.confirm('Enviar el consolidado semanal por correo al administrador ahora?')) return;
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/campaign-tracking/send-weekly-now`, {}, AUTH());
      toast.success(`Reporte enviado: ${res.data.rows} correos consolidados`);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error enviando reporte');
    } finally {
      setSending(false);
    }
  };

  const downloadHistoryItem = async (id, filename) => {
    try {
      const res = await axios.get(`${API_URL}/api/campaign-tracking/history/${id}/download`, {
        ...AUTH(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error('Error descargando reporte');
    }
  };

  // Aggregate stats
  const byType = rows.reduce((acc, r) => { acc[r.tipo] = (acc[r.tipo] || 0) + 1; return acc; }, {});
  const byStatus = rows.reduce((acc, r) => { acc[r.estado] = (acc[r.estado] || 0) + 1; return acc; }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="campaign-tracking-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ChartBar size={28} className="text-blue-600" />
            Control y Seguimiento de Campanas
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Consolidado de correos enviados desde el CRM y B2B. Se envia automaticamente cada lunes 08:00 UTC.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
            data-testid="days-filter"
          >
            <option value={0}>Todo el historial</option>
            <option value={7}>Ultimos 7 dias</option>
            <option value={30}>Ultimos 30 dias</option>
            <option value={90}>Ultimos 90 dias</option>
          </select>
          <button
            onClick={loadPreview}
            className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-1"
            data-testid="refresh-btn"
          >
            <ArrowClockwise size={16} /> Refrescar
          </button>
          <button
            onClick={downloadExcel}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center gap-1.5"
            data-testid="download-excel-btn"
          >
            <Download size={16} /> Descargar Excel
          </button>
          <button
            onClick={sendWeekly}
            disabled={sending}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="send-weekly-btn"
          >
            <Envelope size={16} /> {sending ? 'Enviando...' : 'Enviar Semanal Ahora'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-xs text-slate-500 uppercase font-semibold">Total Correos</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="stat-total">{total}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-xs text-slate-500 uppercase font-semibold">CRM</p>
          <p className="text-2xl font-bold text-blue-700 mt-1">{byType.CRM || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-xs text-slate-500 uppercase font-semibold">B2B</p>
          <p className="text-2xl font-bold text-violet-700 mt-1">{byType.B2B || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-xs text-slate-500 uppercase font-semibold">Rechazados</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{byStatus.RECHAZADO || 0}</p>
        </div>
      </div>

      {/* Status breakdown */}
      <div className="bg-white p-4 rounded-xl border border-slate-200">
        <h3 className="font-bold text-slate-900 mb-3">Estados</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(byStatus).map(([k, v]) => (
            <span key={k} className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[k] || 'bg-slate-100 text-slate-700'}`}>
              {k}: {v}
            </span>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <FileText size={18} />
            Detalle ({rows.length} filas mostradas de {total})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Fecha</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Nombre</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Empresa</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Email</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Telefono</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Sector</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Seguimiento</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Estado</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Documentos</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Tipo</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="text-center py-8 text-slate-400">Cargando...</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={10} className="text-center py-8 text-slate-400">Sin correos en este periodo</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tracking-row-${i}`}>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.fecha}</td>
                  <td className="px-3 py-2 font-medium">{r.nombre || '-'}</td>
                  <td className="px-3 py-2">{r.empresa || '-'}</td>
                  <td className="px-3 py-2 text-blue-700 text-xs">{r.email}</td>
                  <td className="px-3 py-2 text-xs">{r.telefono || '-'}</td>
                  <td className="px-3 py-2 text-xs">{r.sector || '-'}</td>
                  <td className="px-3 py-2 text-xs">{r.fecha_seguimiento || '-'}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[r.estado] || 'bg-slate-100'}`}>
                      {r.estado}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 max-w-[160px] truncate" title={r.documentos}>
                    {r.documentos || '-'}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${r.tipo === 'CRM' ? 'bg-blue-100 text-blue-800' : 'bg-violet-100 text-violet-800'}`}>
                      {r.tipo}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <Calendar size={18} />
            Historial de Reportes Semanales
          </h3>
          <p className="text-xs text-slate-500 mt-1">Reportes consolidados enviados automaticamente cada lunes.</p>
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">
            Aun no hay reportes semanales generados. Se creara el primer lunes automaticamente.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {history.map((h) => (
              <div key={h.id} className="p-3 flex items-center justify-between hover:bg-slate-50" data-testid={`history-item-${h.id}`}>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{h.filename}</p>
                  <p className="text-xs text-slate-500">
                    {h.period_from?.slice(0,10)} a {h.period_to?.slice(0,10)} — {h.row_count} correos
                  </p>
                </div>
                <button
                  onClick={() => downloadHistoryItem(h.id, h.filename)}
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold hover:bg-slate-100 flex items-center gap-1"
                >
                  <Download size={14} /> Descargar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
